import React from 'react';
import { Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface PasswordRequirement {
  label: string;
  met: boolean;
}

interface NotionPasswordStrengthProps {
  password: string;
  showRequirements?: boolean;
}

export function NotionPasswordStrength({ password, showRequirements = true }: NotionPasswordStrengthProps) {
  // Calculate requirements
  const requirements: PasswordRequirement[] = [
    { label: 'Almeno 8 caratteri', met: password.length >= 8 },
    { label: 'Una lettera maiuscola', met: /[A-Z]/.test(password) },
    { label: 'Una lettera minuscola', met: /[a-z]/.test(password) },
    { label: 'Un numero', met: /[0-9]/.test(password) },
  ];

  const metCount = requirements.filter((r) => r.met).length;
  const strength = metCount === 0 ? 0 : metCount / requirements.length;

  // Determine strength level
  const getStrengthLevel = (): { label: string; class: string; color: string } => {
    if (password.length === 0) return { label: '', class: '', color: '' };
    if (metCount <= 1) return { label: 'Debole', class: 'weak', color: '#E03E3E' };
    if (metCount === 2) return { label: 'Sufficiente', class: 'fair', color: '#CB912F' };
    if (metCount === 3) return { label: 'Buona', class: 'good', color: '#6B8E23' };
    return { label: 'Forte', class: 'strong', color: '#0F7B6C' };
  };

  const strengthInfo = getStrengthLevel();

  return (
    <div className="space-y-3">
      {/* Strength bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#787774]">Sicurezza password</span>
          {password.length > 0 && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs font-medium"
              style={{ color: strengthInfo.color }}
            >
              {strengthInfo.label}
            </motion.span>
          )}
        </div>

        <div className="notion-strength-bar">
          <motion.div
            className={`notion-strength-fill ${strengthInfo.class}`}
            initial={{ width: 0 }}
            animate={{ width: `${strength * 100}%` }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      {showRequirements && (
        <div className="grid grid-cols-2 gap-2">
          <AnimatePresence>
            {requirements.map((req, index) => (
              <motion.div
                key={req.label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`notion-requirement ${req.met ? 'met' : ''}`}
              >
                <div
                  className={`
                    notion-requirement-icon
                    ${req.met
                      ? 'bg-[rgba(15,123,108,0.1)]'
                      : 'bg-[rgba(55,53,47,0.06)]'
                    }
                  `}
                >
                  {req.met ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    >
                      <Check className="w-3 h-3 text-[#0F7B6C]" strokeWidth={3} />
                    </motion.div>
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-[#787774]" />
                  )}
                </div>
                <span className="text-xs">{req.label}</span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export default NotionPasswordStrength;
